import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Home,
    Wallet,
    Gamepad2,
    Users,
    Bell,
    ChevronLeft,
    Trophy,
    MessageCircle,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import HeaderIconButton, {
    HeaderActionsCluster,
} from "@/components/shared/HeaderIconButton";
import UserAvatar from "@/components/UserAvatar";
import { hasCapability } from "@/config/capabilities";

// Navigation items - filtered by capabilities at render time
// Profile moved to TopHeader, Achievements added to bottom nav
const allNavItems = [
    { path: "/home", icon: Home, label: "Home", capability: null },
    {
        path: "/wallet",
        icon: Wallet,
        label: "Wallet",
        capability: "client.wallet",
    },
    { path: "/", icon: Gamepad2, label: "Games", capability: "client.games" },
    {
        path: "/referrals",
        icon: Users,
        label: "Referrals",
        capability: "client.referrals",
    },
    {
        path: "/messages",
        icon: MessageCircle,
        label: "Chat",
        capability: "client.chat",
    },
];

export function BottomNav() {
    const location = useLocation();
    const navItems = allNavItems.filter(
        (item) => !item.capability || hasCapability(item.capability),
    );

    return (
        <nav
            className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-white/5"
            data-testid="bottom-nav"
        >
            <div className="max-w-4xl mx-auto px-2 py-2 pb-safe">
                <div className="flex items-center justify-around">
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        const Icon = item.icon;

                        return (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className="relative flex flex-col items-center py-2 px-4 tap-scale no-select"
                                data-testid={`nav-${item.label.toLowerCase()}`}
                            >
                                <div className="relative">
                                    {isActive && (
                                        <motion.div
                                            layoutId="navIndicator"
                                            className="absolute -inset-2 bg-gradient-to-br from-violet-500/30 to-cyan-500/30 rounded-xl"
                                            transition={{
                                                type: "spring",
                                                bounce: 0.2,
                                                duration: 0.4,
                                            }}
                                        />
                                    )}
                                    <Icon
                                        className={`relative w-5 h-5 transition-colors duration-200 ${
                                            isActive
                                                ? "text-white"
                                                : "text-gray-500"
                                        }`}
                                    />
                                </div>
                                <span
                                    className={`text-[10px] mt-1 font-medium transition-colors duration-200 ${
                                        isActive
                                            ? "text-white"
                                            : "text-gray-500"
                                    }`}
                                >
                                    {item.label}
                                </span>
                            </NavLink>
                        );
                    })}
                </div>
            </div>
        </nav>
    );
}

export function TopHeader({
    title,
    showInbox = true,
    rightAction,
    leftAction,
    showBack = false,
    backTo,
    extraBelow = null,
    headerExtraClass = "",
}) {
    const { unreadCount, user } = useApp();
    const location = useLocation();
    const navigate = useNavigate();

    // Build left-side content: explicit leftAction wins, then showBack, then nothing
    const leftContent =
        leftAction ||
        (showBack ? (
            <button
                onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
                className="p-1.5 -ml-1.5 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="header-back-button"
                aria-label="Go back"
            >
                <ChevronLeft className="w-5 h-5 text-gray-300" />
            </button>
        ) : null);

    return (
        <header
            className={`sticky top-0 z-30 glass border-b border-white/5 ${headerExtraClass}`}
        >
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    {leftContent}
                    <h1 className="text-lg font-bold gradient-primary-text truncate">
                        {title}
                    </h1>
                </div>
                <div
                    className="flex items-center gap-2 flex-shrink-0"
                    data-testid="top-header-actions"
                >
                    {/* Page-provided right-action slot — rendered first so status cluster stays adjacent to avatar */}
                    {rightAction}

                    {/* Status cluster (notifications) — visually grouped so future
                        announcement buttons can sit here without crowding the avatar */}
                    {showInbox && (
                        <HeaderActionsCluster>
                            <HeaderIconButton
                                as={NavLink}
                                to="/achievements?tab=leaderboards"
                                icon={Trophy}
                                title="Leaderboard"
                                label="Leaderboard"
                                testId="topnav-leaderboard-button"
                                variant="subtle"
                            />
                            <HeaderIconButton
                                as={NavLink}
                                to="/inbox"
                                icon={Bell}
                                title="Notifications"
                                label="Notifications"
                                testId="inbox-button"
                                badgeCount={unreadCount}
                                badgeColor="violet"
                                variant="subtle"
                            />
                        </HeaderActionsCluster>
                    )}

                    {/* Profile — real avatar (or initials fallback) so a
                        signed-up user immediately sees their uploaded photo
                        in the header instead of a generic icon. */}
                    <NavLink
                        to="/profile"
                        className="relative active:scale-95 transition-transform"
                        data-testid="profile-button"
                        aria-label="Profile"
                        title="Profile"
                    >
                        <UserAvatar
                            user={user}
                            size="md"
                            className="border border-white/10 hover:border-white/25 transition-colors"
                            data-testid="profile-button-avatar"
                        />
                    </NavLink>
                </div>
            </div>
            {/* Optional second row (e.g. chat search) — inside the same sticky
                glass container so the title + extra content read as one header. */}
            {extraBelow && (
                <div className="max-w-6xl mx-auto">{extraBelow}</div>
            )}
        </header>
    );
}

export function PageTransition({ children }) {
    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
        >
            {children}
        </motion.div>
    );
}
